import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useAppDispatch } from '../store/hooks';
import { saveUserSession } from '../store/authSlice';
import AuthService from '../services/auth';

interface NicknameLoginScreenProps {
  onBack: () => void;
}

const NicknameLoginScreen: React.FC<NicknameLoginScreenProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true); // 默认显示注册，因为这是新用户友好的方式

  // 验证昵称格式
  const validateNickname = (name: string): boolean => {
    return name.length >= 2 && name.length <= 20 && /^[a-zA-Z0-9\u4e00-\u9fa5_-]+$/.test(name);
  };

  // 注册
  const handleSignUp = async () => {
    if (!validateNickname(nickname)) {
      Alert.alert('错误', '昵称应为2-20个字符，只能包含字母、数字、中文、下划线和连字符');
      return;
    }

    if (password.length < 6) {
      Alert.alert('错误', '密码长度至少6位');
      return;
    }

    try {
      setLoading(true);
      const user = await AuthService.signUpWithNickname(nickname, password);
      dispatch(saveUserSession(user));
      
      // 检查是否是离线模式注册
      const message = user.id.includes('offline') || console.log.toString().includes('离线') 
        ? `欢迎 ${nickname}！账号已创建（离线模式），网络恢复后将自动同步。`
        : `欢迎 ${nickname}！注册成功，开始您的语言学习之旅吧！`;
        
      Alert.alert('成功', message);
    } catch (error: any) {
      console.error('Nickname signup error:', error);
      const errorMessage = error.message?.includes('网络') 
        ? `${error.message}\n\n提示：您可以稍后网络恢复时重试注册`
        : error.message || '注册失败，请重试';
      Alert.alert('注册失败', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 登录
  const handleSignIn = async () => {
    if (!nickname.trim()) {
      Alert.alert('错误', '请输入昵称');
      return;
    }

    if (!password.trim()) {
      Alert.alert('错误', '请输入密码');
      return;
    }

    try {
      setLoading(true);
      const user = await AuthService.signInWithNickname(nickname, password);
      dispatch(saveUserSession(user));
      Alert.alert('成功', `欢迎回来，${nickname}！`);
    } catch (error: any) {
      console.error('Nickname login error:', error);
      Alert.alert('登录失败', error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.content}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← 返回</Text>
        </TouchableOpacity>

        <View style={styles.header}>
          <Text style={styles.title}>{isSignUp ? '开始学习' : '欢迎回来'}</Text>
          <Text style={styles.subtitle}>
            {isSignUp ? '只需昵称和密码，立即开始学习' : '输入您的昵称和密码'}
          </Text>
        </View>

        <View style={styles.featureBox}>
          <Text style={styles.featureTitle}>🎮 简单快捷</Text>
          <Text style={styles.featureText}>
            无需邮箱验证，昵称+密码即可开始学习
          </Text>
          <Text style={styles.featureText}>
            需要高级功能时可稍后验证邮箱或手机
          </Text>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>昵称</Text>
          <TextInput
            style={styles.input}
            placeholder="请输入昵称（2-20个字符）"
            value={nickname}
            onChangeText={setNickname}
            autoCapitalize="none"
            maxLength={20}
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>密码</Text>
          <TextInput
            style={styles.input}
            placeholder="请输入密码（至少6位）"
            value={password}
            onChangeText={setPassword}
            secureTextEntry={true}
            autoCapitalize="none"
          />
        </View>

        <TouchableOpacity
          style={[
            styles.button,
            ((!validateNickname(nickname) && isSignUp) || !nickname.trim() || password.length < 6 || loading) && styles.buttonDisabled,
          ]}
          onPress={isSignUp ? handleSignUp : handleSignIn}
          disabled={
            ((!validateNickname(nickname) && isSignUp) || !nickname.trim() || password.length < 6 || loading)
          }
        >
          <Text style={styles.buttonText}>
            {loading ? (isSignUp ? '注册中...' : '登录中...') : (isSignUp ? '立即注册' : '登录')}
          </Text>
        </TouchableOpacity>

        <View style={styles.switchContainer}>
          <Text style={styles.switchText}>
            {isSignUp ? '已有账号？' : '还没有账号？'}
          </Text>
          <TouchableOpacity onPress={() => setIsSignUp(!isSignUp)}>
            <Text style={styles.switchLink}>
              {isSignUp ? '立即登录' : '创建账号'}
            </Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.disclaimer}>
          注册即表示同意用户协议和隐私政策
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
  content: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  backButton: {
    position: 'absolute',
    top: 50,
    left: 20,
    zIndex: 1,
  },
  backButtonText: {
    fontSize: 16,
    color: '#4285F4',
    fontWeight: '600',
  },
  header: {
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  featureBox: {
    backgroundColor: '#e8f5e8',
    borderColor: '#4caf50',
    borderWidth: 1,
    borderRadius: 8,
    padding: 15,
    marginBottom: 30,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2e7d32',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#2e7d32',
    lineHeight: 20,
    marginBottom: 4,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    color: '#333',
    marginBottom: 8,
    fontWeight: '600',
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 15,
    fontSize: 16,
    backgroundColor: '#fff',
    color: '#333',
  },
  button: {
    backgroundColor: '#4285F4',
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  switchText: {
    fontSize: 14,
    color: '#666',
    marginRight: 5,
  },
  switchLink: {
    fontSize: 14,
    color: '#4285F4',
    fontWeight: '600',
  },
  disclaimer: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 16,
  },
});

export default NicknameLoginScreen;
